import { useEffect, useState } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Wrench, Clock, Search } from "lucide-react";
import { toast } from "sonner";
import ImportExportBar from "@/components/ImportExportBar";
import { PaginationBar, usePagination } from "@/components/Pagination";

const empty = { name: "", labour_rate: 0, category: "", group: "", estimated_minutes: 30, description: "" };

export default function JobRates() {
  const [list, setList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const load = () => api.get("/job-types").then(r => setList(r.data));
  useEffect(() => {
    load();
    api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
    api.get("/groups").then(r => setGroups(r.data)).catch(() => {});
  }, []);

  // Groups filtered by selected category (Category master carries its linked group)
  const catGroup = f.category ? (categories.find(c => c.name === f.category)?.group || "") : "";
  const groupOpts = catGroup ? groups.filter(g => g.name === catGroup) : groups;

  const onCategoryChange = (v) => {
    const linked = categories.find(c => c.name === v)?.group || "";
    setF(x => ({ ...x, category: v, group: linked }));
  };

  const save = async () => {
    try {
      const payload = {
        ...f,
        category: f.category || "general",
        group: f.group || null,
        labour_rate: Number(f.labour_rate),
        estimated_minutes: Number(f.estimated_minutes),
      };
      if (editId) await api.patch(`/job-types/${editId}`, payload);
      else await api.post("/job-types", payload);
      toast.success(editId ? "Rate updated" : "Job type added");
      setF(empty); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const edit = (jt) => {
    setF({ name: jt.name, labour_rate: jt.labour_rate, category: jt.category || "",
           group: jt.group || "", estimated_minutes: jt.estimated_minutes || 30, description: jt.description || "" });
    setEditId(jt.id); setOpen(true);
  };

  const del = async (id) => {
    if (!confirm("Delete this job type?")) return;
    try { await api.delete(`/job-types/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const visible = search
    ? list.filter(jt => [jt.name, jt.category, jt.group, jt.description].some(v => (v || "").toLowerCase().includes(search.toLowerCase())))
    : list;
  const pg = usePagination(visible, search);

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Wrench className="w-3 h-3"/> Owner / Manager only</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Job Rate Card</h1>
          <div className="text-sm text-muted-foreground mt-2">Fixed labour rates that auto-fill on job cards when a type is picked</div>
        </div>
        <div className="flex items-center gap-2">
          <ImportExportBar masterKey="job-types" filters={{ search, from_date: fromDate, to_date: toDate }} onImported={load} />
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF(empty); setEditId(null); }}}>
          <DialogTrigger asChild>
            <Button data-testid="add-jobtype-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
              <Plus className="w-4 h-4 mr-1"/> New Job Type
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm bg-card border-border max-w-lg">
            <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} Job Type</DialogTitle></DialogHeader>
            <div className="space-y-3" onKeyDownCapture={handleSimpleFormNav}>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Name</Label>
                <Input data-testid="jt-name" value={f.name} onChange={(e) => setF(x => ({ ...x, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder="e.g. Oil Change" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Category</Label>
                  <Select value={f.category} onValueChange={onCategoryChange}>
                    <SelectTrigger data-testid="jt-category" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select category"/></SelectTrigger>
                    <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.name}>{c.category_code ? `${c.category_code} · ${c.name}` : c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Group</Label>
                  <Select value={f.group} onValueChange={(v) => setF(x => ({ ...x, group: v }))}>
                    <SelectTrigger data-testid="jt-group" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder={f.category ? "Auto from category" : "Select group"}/></SelectTrigger>
                    <SelectContent>{groupOpts.map(g => <SelectItem key={g.id} value={g.name}>{g.group_code ? `${g.group_code} · ${g.name}` : g.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Labour Rate (₹)</Label>
                  <Input data-testid="jt-rate" type="number" value={f.labour_rate} onChange={(e) => setF(x => ({ ...x, labour_rate: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Est. Minutes</Label>
                  <Input data-testid="jt-minutes" type="number" value={f.estimated_minutes} onChange={(e) => setF(x => ({ ...x, estimated_minutes: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Description (optional)</Label>
                <Textarea data-testid="jt-desc" value={f.description} onChange={(e) => setF(x => ({ ...x, description: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2} />
              </div>
              <Button data-testid="save-jobtype-btn" onClick={save} disabled={!f.name || !f.labour_rate} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
                {editId ? "Update" : "Save"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-6 border border-border bg-card p-3 rounded-sm">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Search</Label>
          <div className="relative mt-1">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground"/>
            <Input data-testid="jt-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, category, group, description…" className="pl-7 rounded-sm bg-secondary border-border h-9"/>
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">From</Label>
          <Input data-testid="jt-from-date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border h-9"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">To</Label>
          <Input data-testid="jt-to-date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border h-9"/>
        </div>
        <div className="text-[10px] text-muted-foreground italic pb-2">Filters apply to CSV / Excel / PDF exports</div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Category</th>
              <th className="text-left p-3">Group</th>
              <th className="text-right p-3">Labour Rate</th>
              <th className="text-left p-3">Est. Time</th>
              <th className="text-left p-3">Description</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No job types yet. Add one to enable auto-fill on job cards.</td></tr>}
            {pg.pageItems.map(jt => (
              <tr key={jt.id} data-testid={`jobtype-${jt.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-display font-bold uppercase tracking-tight">{jt.name}</td>
                <td className="p-3 text-xs">{jt.category || "-"}</td>
                <td className="p-3 text-xs">{jt.group || "-"}</td>
                <td className="p-3 text-right font-mono text-primary">{inr(jt.labour_rate)}</td>
                <td className="p-3 text-xs text-muted-foreground">
                  {jt.estimated_minutes ? <span className="flex items-center gap-1"><Clock className="w-3 h-3"/> {jt.estimated_minutes} min</span> : "-"}
                </td>
                <td className="p-3 text-xs text-muted-foreground max-w-56 truncate" title={jt.description}>{jt.description || "-"}</td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  <Button data-testid={`edit-${jt.id}`} size="sm" variant="ghost" onClick={() => edit(jt)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                  <Button data-testid={`del-${jt.id}`} size="sm" variant="ghost" onClick={() => del(jt.id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
